import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Checkbox,
  Button,
  Stack,
  Tooltip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { get } from '../../utils/api';
import ThankYouEmailDialog from '../reports/ThankYouEmailDialog';
import RecurringPledgeIndicator from './RecurringPledgeIndicator';
import { isInternalNoEmail, sanitizeEmail } from '../../utils/customerEmail';

function formatAmount(n) {
  const v = Number(n);
  if (!isFinite(v)) return '--';
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Pledges Customer Dialog - for PledgesPage (handles status filter: all/pending/completed)
export function PledgesCustomerDialog({ open, onClose, product, statusFilter = 'all', onOpenOrderEdit }) {
  const [rows, setRows] = React.useState([]);
  const [loadingRows, setLoadingRows] = React.useState(false);
  const [expandedCustomers, setExpandedCustomers] = React.useState({});
  const [customerTransactions, setCustomerTransactions] = React.useState({});
  const [loadingTransactions, setLoadingTransactions] = React.useState({});
  const [selectedCustomerKeys, setSelectedCustomerKeys] = React.useState({});
  const [bulkEmailOpen, setBulkEmailOpen] = React.useState(false);
  const [singleEmailOpen, setSingleEmailOpen] = React.useState(false);
  const [singleEmailConfig, setSingleEmailConfig] = React.useState(null);
  const [emailMessage, setEmailMessage] = React.useState('');
  const [productPaymentLink, setProductPaymentLink] = React.useState('');
  const [hidePaid, setHidePaid] = React.useState(false);
  const [hideCompleted, setHideCompleted] = React.useState(false);
  const resolvePaymentLink = React.useCallback(() => {
    const pid = product?.product_id;
    if (productPaymentLink) return productPaymentLink;
    if (!pid) return '';
    return '';
  }, [product, productPaymentLink]);

  // Load pledges when dialog opens or product/status changes
  React.useEffect(() => {
    if (!open || !product) {
      setRows([]);
      setLoadingRows(false);
      setExpandedCustomers({});
      setCustomerTransactions({});
      setLoadingTransactions({});
      setSelectedCustomerKeys({});
      setEmailMessage('');
      setHidePaid(false);
      setHideCompleted(false);
      return;
    }

    const loadPledges = async () => {
      setLoadingRows(true);
      try {
        const pid = product.product_id;
        const vid = product.variation?.variation_id;
        try {
          const prodRes = await get(`/ldb/products/${pid}`);
          const link = String(
            prodRes?.product?.product_url ||
            prodRes?.product_url ||
            prodRes?.product?.permalink ||
            prodRes?.permalink ||
            ''
          ).trim();
          setProductPaymentLink(link);
        } catch {
          setProductPaymentLink('');
        }
        
        if (product.variation && vid !== null && vid !== undefined) {
          // Pass status filter to backend
          const params = new URLSearchParams();
          params.append('status', statusFilter || 'all');
          const res = await get(`/ldb/reports/pledges/by-product/${pid}/variations/${vid}/customers?${params.toString()}`);
          const data = Array.isArray(res?.data) ? res.data : [];
          console.log('[PledgesCustomerDialog] Loaded', data.length, 'customers for product', pid, 'variation', vid, 'status', statusFilter);
          setRows(data);
        } else {
          setRows([]);
        }
      } catch (e) {
        console.error('[PledgesCustomerDialog] Failed to load pledges:', e);
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    };

    loadPledges();
  }, [open, product, statusFilter]);

  const getCustomerKey = React.useCallback((r) => {
    const email = sanitizeEmail(r?.email);
    if (email) return `email:${email}`;
    if (r?.customer_id) return `cust:${r.customer_id}`;
    return `idx:${r?.id || r?.name || ''}`;
  }, []);

  const getRowEmail = React.useCallback((r) => sanitizeEmail(r?.email || r?.billing_email || ''), []);

  const isEmailSelectable = React.useCallback((r) => {
    const email = getRowEmail(r);
    if (!email || !email.includes('@')) return false;
    if (isInternalNoEmail(email)) return false;
    return true;
  }, [getRowEmail]);

  const selectedRows = React.useMemo(() => {
    return rows.filter((r) => selectedCustomerKeys[getCustomerKey(r)]);
  }, [rows, selectedCustomerKeys, getCustomerKey]);

  const selectedRecipients = React.useMemo(() => {
    const out = new Set();
    selectedRows.forEach((r) => {
      const email = getRowEmail(r);
      if (!email || !email.includes('@') || isInternalNoEmail(email)) return;
      out.add(email);
    });
    return Array.from(out);
  }, [selectedRows, getRowEmail]);

  const selectedRecipientContexts = React.useMemo(() => {
    const out = {};
    selectedRows.forEach((r) => {
      const email = getRowEmail(r);
      if (!email || !email.includes('@') || isInternalNoEmail(email)) return;
      out[email] = {
        customer_id: r.customer_id,
        product_id: product?.product_id,
        variation_id: product?.variation?.variation_id ?? 0,
        recipientName: r.name || 'Devotee',
        pledgeId: r.pledge_id || '',
        productName: product?.variation?.variation_name || product?.product_name || 'Pledge',
        variationName: product?.variation?.variation_name || '',
        pledgedAmount: Number(r.pledge_total || 0),
        paymentsMade: Number(r.paid_total || 0),
        balanceAmount: Number(r.balance_total || 0),
        paymentLink: resolvePaymentLink(),
      };
    });
    return out;
  }, [selectedRows, getRowEmail, product, resolvePaymentLink]);

  // Sort by balance descending; optionally hide paid (balance <= 0) and hide completed (pledge-completed)
  const displayRows = React.useMemo(() => {
    let list = [...rows].sort((a, b) => Number(b.balance_total || 0) - Number(a.balance_total || 0));
    if (hidePaid) list = list.filter((r) => Number(r.balance_total || 0) > 0);
    if (hideCompleted) list = list.filter((r) => String(r.pledge_status || '').toLowerCase() !== 'pledge-completed');
    return list;
  }, [rows, hidePaid, hideCompleted]);

  const selectableRows = React.useMemo(() => displayRows.filter((r) => isEmailSelectable(r)), [displayRows, isEmailSelectable]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedCustomerKeys[getCustomerKey(r)]);

  const toggleRowSelection = (r, checked) => {
    const key = getCustomerKey(r);
    setSelectedCustomerKeys((prev) => {
      const next = { ...prev };
      if (checked) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedCustomerKeys({});
      return;
    }
    const next = {};
    selectableRows.forEach((r) => { next[getCustomerKey(r)] = true; });
    setSelectedCustomerKeys(next);
  };

  const openSingleEmailDialog = (r) => {
    const email = getRowEmail(r);
    if (!email || !email.includes('@') || isInternalNoEmail(email)) return;
    const itemName = product?.variation?.variation_name || product?.product_name || 'Pledge';
    setSingleEmailConfig({
      recipients: [email],
      context: {
        customer_id: r.customer_id,
        product_id: product?.product_id,
        variation_id: product?.variation?.variation_id ?? 0,
        recipientName: r.name || 'Devotee',
        pledgeId: r.pledge_id || '',
        productName: itemName,
        variationName: product?.variation?.variation_name || '',
        pledgedAmount: Number(r.pledge_total || 0),
        paymentsMade: Number(r.paid_total || 0),
        balanceAmount: Number(r.balance_total || 0),
        paymentLink: resolvePaymentLink(),
      },
    });
    setSingleEmailOpen(true);
  };

  const toggleCustomerTransactions = async (customerKey, customer) => {
    const isExpanded = !!expandedCustomers[customerKey];
    if (isExpanded) {
      setExpandedCustomers(prev => {
        const next = { ...prev };
        delete next[customerKey];
        return next;
      });
      return;
    }

    setExpandedCustomers(prev => ({ ...prev, [customerKey]: true }));
    
    if (customerTransactions[customerKey]) {
      return;
    }

    setLoadingTransactions(prev => ({ ...prev, [customerKey]: true }));
    
    try {
      const pid = product?.product_id;
      const vid = product?.variation?.variation_id;
      
      if (!pid) {
        setLoadingTransactions(prev => {
          const next = { ...prev };
          delete next[customerKey];
          return next;
        });
        return;
      }

      let allOrders = [];
      let pageNum = 1;
      const perPageNum = 100;
      
      while (true) {
        const queryParams = [
          `status=completed`,
          `product_id=${encodeURIComponent(pid)}`,
          `per_page=${perPageNum}`,
          `page=${pageNum}`
        ];
        
        if (customer.customer_id) {
          queryParams.push(`customer_id=${encodeURIComponent(customer.customer_id)}`);
        }
        if (customer.email) {
          queryParams.push(`email=${encodeURIComponent(customer.email)}`);
        }
        
        const res = await get(`/ldb/orders/search?${queryParams.join('&')}`);
        const batch = Array.isArray(res?.data) ? res.data : [];
        
        const enriched = await Promise.all(batch.map(async (o) => {
          try {
            const det = await get(`/ldb/orders/${o.id}`);
            const orderData = det?.order || det || {};
            return { 
              ...o, 
              ...orderData,
              items: Array.isArray(det?.items) ? det.items : [],
              payment_method: orderData.payment_method || o.payment_method || '',
              payment_method_title: orderData.payment_method_title || o.payment_method_title || ''
            };
          } catch {
            return { ...o, items: [] };
          }
        }));
        
        const donorKey = (r) => {
          const em = String(r.billing_email || '').trim().toLowerCase();
          if (em && em !== '') return em;
          if (r.customer_id) return `cust#${r.customer_id}`;
          return `order#${r.id || 0}`;
        };
        
        const filtered = enriched.filter(o => {
          const orderKey = donorKey(o);
          if (orderKey !== customerKey) return false;
          
          const items = o.items || o.line_items || [];
          return items.some(item => {
            const itemPid = item.product_id || item.productId || '';
            if (String(itemPid) !== String(pid)) return false;
            if (vid !== null && vid !== undefined) {
              const itemVid = item.variation_id || item.variationId || 0;
              return String(itemVid || 0) === String(vid);
            }
            return true;
          });
        });
        
        allOrders = allOrders.concat(filtered);
        
        if (batch.length < perPageNum) break;
        pageNum += 1;
        if (pageNum > 50) break;
      }
      
      const transactions = allOrders.flatMap(o => {
        const items = o.items || o.line_items || [];
        return items
          .filter(item => {
            const itemPid = item.product_id || item.productId || '';
            if (String(itemPid) !== String(pid)) return false;
            if (vid !== null && vid !== undefined) {
              const itemVid = item.variation_id || item.variationId || 0;
              return String(itemVid || 0) === String(vid);
            }
            return true;
          })
          .map(item => ({
            orderId: o.id,
            date: o.date_created_gmt || o.date_created,
            status: o.status,
            amount: Number(item.total_num || item.total || item.subtotal || 0),
            quantity: Number(item.quantity || 0),
            paymentMethod: o.payment_method_title || o.payment_method || ''
          }));
      }).sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setCustomerTransactions(prev => ({ ...prev, [customerKey]: transactions }));
    } catch (e) {
      console.error('[PledgesCustomerDialog] Failed to load customer transactions:', e);
      setCustomerTransactions(prev => ({ ...prev, [customerKey]: [] }));
    } finally {
      setLoadingTransactions(prev => {
        const next = { ...prev };
        delete next[customerKey];
        return next;
      });
    }
  };

  // Totals for displayed rows only
  const totals = React.useMemo(() => displayRows.reduce((acc, r) => ({
    pledge_total: acc.pledge_total + Number(r.pledge_total || 0),
    paid_total: acc.paid_total + Number(r.paid_total || 0),
    balance_total: acc.balance_total + Number(r.balance_total || 0),
    count: acc.count + 1
  }), { pledge_total: 0, paid_total: 0, balance_total: 0, count: 0 }), [displayRows]);

  const title = product
    ? (product.variation
        ? `${product.product_name || `#${product.product_id}`} — ${product.variation.variation_name || `Variation #${product.variation.variation_id}`}`
        : (product.product_name || `#${product.product_id}`))
    : 'Pledges';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        {title} — Devotees
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: '600px', maxHeight: '80vh', overflow: 'auto' }}>
        {emailMessage ? (
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setEmailMessage('')}>
            {emailMessage}
          </Alert>
        ) : null}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
            Select devotee rows to send group pledge communication.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={hidePaid}
                onChange={(e) => setHidePaid(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Hide paid (show only balance due)</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Hide completed (show only pending)</Typography>}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<MailOutlineIcon />}
            onClick={() => setBulkEmailOpen(true)}
            disabled={selectedRecipients.length === 0}
          >
            Send Email ({selectedRecipients.length})
          </Button>
        </Stack>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '40px minmax(180px,1fr) 100px 120px 120px 120px 100px 56px', bgcolor: 'action.hover' }}>
            <Box sx={{ p: 0.5, display: 'flex', justifyContent: 'center' }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={!allSelected && Object.keys(selectedCustomerKeys).length > 0}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
            </Box>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>Devotee</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'center' }}>Status</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Pledge</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Paid</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Balance</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'center' }}>Order #</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'center' }}>Email</Typography>
          </Box>
          {/* Totals row */}
          {displayRows.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '40px minmax(180px,1fr) 100px 120px 120px 120px 100px 56px', borderTop: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.light', '& *': { color: 'primary.contrastText', fontWeight: 700 } }}>
              <Box />
              <Typography variant="body2" sx={{ p: 1 }}>
                Total ({totals.count} {totals.count === 1 ? 'devotee' : 'devotees'})
              </Typography>
              <Box />
              <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(totals.pledge_total)}</Typography>
              <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(totals.paid_total)}</Typography>
              <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(totals.balance_total)}</Typography>
              <Box />
              <Box />
            </Box>
          )}
          <Box>
            {displayRows.map((r, i) => {
              const email = String(r.email || '').trim().toLowerCase();
              const customerKey = email ? email : (r.customer_id ? `cust#${r.customer_id}` : `order#${r.customer_id || 0}`);
              const isExpanded = !!expandedCustomers[customerKey];
              const transactions = customerTransactions[customerKey] || [];
              const isLoading = !!loadingTransactions[customerKey];
              const statusLabel = String(r.pledge_status || '').toLowerCase() === 'pledge-completed' ? 'Completed' : String(r.pledge_status || '').toLowerCase() === 'pledge-pending' ? 'Pending' : (r.pledge_status || '—');
              const orderId = r.pledge_order_id != null ? Number(r.pledge_order_id) : null;
              
              return (
                <React.Fragment key={`${r.customer_id}-${i}`}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px minmax(180px,1fr) 100px 120px 120px 120px 100px 56px',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                    onClick={() => toggleCustomerTransactions(customerKey, r)}
                  >
                    <Box sx={{ p: 0.5, display: 'flex', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={!!selectedCustomerKeys[getCustomerKey(r)]}
                        disabled={!isEmailSelectable(r)}
                        onChange={(e) => toggleRowSelection(r, e.target.checked)}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ p: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      <span style={{ color: '#1976d2', fontWeight: 500 }}>
                        {r.name || r.email || `#${r.customer_id}`}
                      </span>
                      <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}><RecurringPledgeIndicator recurring={r.recurring} /></Box>
                    </Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'center' }}>{statusLabel}</Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'right' }}>{formatAmount(r.pledge_total)}</Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'right' }}>{formatAmount(r.paid_total)}</Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'right', fontWeight: 600 }}>{formatAmount(r.balance_total)}</Typography>
                    <Box sx={{ p: 0.25, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {orderId != null ? (
                        typeof onOpenOrderEdit === 'function' ? (
                          <Link component="button" variant="body2" onClick={() => onOpenOrderEdit(orderId)} sx={{ cursor: 'pointer' }}>
                            #{orderId}
                          </Link>
                        ) : (
                          <Typography variant="body2">#{orderId}</Typography>
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </Box>
                    <Box sx={{ p: 0.25, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {isEmailSelectable(r) ? (
                        <Tooltip title="Send email to this devotee">
                          <IconButton size="small" onClick={() => openSingleEmailDialog(r)}>
                            <MailOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </Box>
                  {isExpanded && (
                    <Box sx={{ pl: 3, pr: 1, pb: 1, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                      {isLoading ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Loading transactions...</Typography>
                      ) : transactions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No transactions found</Typography>
                      ) : (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>Transactions:</Typography>
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px 120px', bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700 }}>Date</Typography>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700 }}>Order #</Typography>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700 }}>Payment Method</Typography>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700, textAlign: 'right' }}>Amount</Typography>
                            </Box>
                            {transactions.map((txn, txnIdx) => (
                              <Box key={txnIdx} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px 120px', borderTop: txnIdx > 0 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: txnIdx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem' }}>
                                  {txn.date ? new Date(txn.date).toLocaleDateString() : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem' }}>
                                  #{txn.orderId}
                                </Typography>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem' }}>
                                  {txn.paymentMethod || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                                  {formatAmount(txn.amount)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
            {loadingRows ? (
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading devotees...</Typography>
              </Box>
            ) : rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No data</Typography>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <ThankYouEmailDialog
        open={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        reportType="pledges"
        initialEmailType="pledge_payment_reminder"
        context={{
          productName: product?.product_name || '',
          variationName: product?.variation?.variation_name || '',
        }}
        recipients={selectedRecipients}
        recipientContexts={selectedRecipientContexts}
        onSent={(res) => {
          const sentCount = Number(res?.recipient_count || selectedRecipients.length);
          setEmailMessage(`Pledge email sent successfully to ${sentCount} recipient(s).`);
          setBulkEmailOpen(false);
        }}
      />
      <ThankYouEmailDialog
        open={singleEmailOpen}
        onClose={() => { setSingleEmailOpen(false); setSingleEmailConfig(null); }}
        reportType="pledges"
        initialEmailType="pledge_payment_reminder"
        context={singleEmailConfig?.context || {}}
        recipients={singleEmailConfig?.recipients || []}
        recipientContexts={singleEmailConfig?.recipients?.[0] ? { [singleEmailConfig.recipients[0]]: singleEmailConfig.context || {} } : {}}
        onSent={() => {
          setEmailMessage('Pledge email sent successfully.');
          setSingleEmailOpen(false);
          setSingleEmailConfig(null);
        }}
      />
    </Dialog>
  );
}

